import posthog from "posthog-js";

type IdentifyProps = {
  id: string;
  email?: string;
  company?: { id?: string; isConsultant?: boolean } | null;
};

let initialized = false;

// ---------------------------------------------------------------------------
// PII scrubbers for chat content surfaces.
//
// We capture the *verbatim* text of user prompts and assistant replies so
// the chat-quality dashboards can correlate event volume against the same
// content the eval harness scores. That means PII has to be stripped at
// the client boundary — once it lands in PostHog the cost of removing it
// is operational, not technical. Two patterns cover the long tail of
// what users actually type into a B2B compliance tool:
//
//   1. Email addresses — owners pasting "send to jdoe@acme.com" into a
//      forward-this-to-my-RDP follow-up.
//   2. Phone numbers — US 10-digit shapes, with or without country code,
//      with or without parens/dashes/dots/spaces.
//
// The patterns intentionally err on the side of FALSE POSITIVE (more
// scrubbing than strictly needed) over false negative. A literal
// 10-digit street-address number like "5550000000" gets <phone>'d; we
// accept that — it's noise in the analytics anyway, and the alternative
// (leaking a real number) is worse.
//
// NOT scrubbed (deferred): names, building addresses, BBLs, BINs. These
// are the load-bearing nouns of every chat turn ("penalty for 170
// Broadway", "LL97 status of BBL 1-00123-0001"); scrubbing them would
// blank the analytic surface. Names are rare in product chat (this is
// not a CRM); addresses are explicitly the subject matter. If a
// customer pastes a 9-digit SSN we don't catch it — they shouldn't be
// pasting it into a building-compliance chat in the first place, and
// the broader org has DLP coverage upstream.
// ---------------------------------------------------------------------------
const PII_PATTERNS: ReadonlyArray<{ pattern: RegExp; replacement: string }> = [
  {
    pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    replacement: "<email>",
  },
  {
    pattern: /\b(?:\+?1[-.]?)?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
    replacement: "<phone>",
  },
];

const scrubPII = (text: string): string =>
  PII_PATTERNS.reduce(
    (acc, { pattern, replacement }) => acc.replace(pattern, replacement),
    text,
  );

// Keys whose values are user / model prose and must be PII-scrubbed
// before going on the wire. Anything else passes through as-is —
// caller-emitted thread_ids, latencies, building_ids, etc. are not PII.
const PII_SCRUB_KEYS: ReadonlySet<string> = new Set([
  "seed_prompt",
  "message_content",
  "response_text",
]);

// ---------------------------------------------------------------------------
// Session-recording unmask predicate.
//
// Today's config is ``maskAllInputs: true`` — every input + textarea is
// masked in replay. That defeats the chat-quality goal of replaying
// real prompts. Option B (per Leo's call): keep the global mask, opt
// OUT on chat-only surfaces by tagging them with the HTML attribute
// ``data-ph-no-mask-input``. Rrweb does not natively read that
// attribute, so we plug into ``maskInputFn`` and walk up the DOM tree
// looking for it — if any ancestor (or the element itself) carries the
// attribute, return the input value unmasked.
//
// Why a closest()-style walk and not a single selector? React portals
// (dialogs, popovers) can detach the input from its semantic ancestor.
// Walking the live DOM tree at recording time covers both inline and
// portaled surfaces without a second config knob.
//
// Performance: maskInputFn is called per input keystroke. closest() is
// O(ancestors); chat composers sit ~5-10 levels deep — well under a
// microsecond. Not a hot path.
//
// ESPM connect, login, payment, and every other surface that doesn't
// add the attribute continue to mask as before. Default-deny.
// ---------------------------------------------------------------------------
const UNMASK_ATTRIBUTE = "data-ph-no-mask-input";

const isUnmaskedElement = (element?: HTMLElement): boolean => {
  if (!element) return false;
  try {
    return element.closest(`[${UNMASK_ATTRIBUTE}]`) !== null;
  } catch {
    return false;
  }
};

const maskInputFn = (text: string, element?: HTMLElement): string => {
  if (isUnmaskedElement(element)) return text;
  // Default rrweb-style mask: a string of asterisks the same length so
  // replay UI doesn't collapse the input visually.
  return "*".repeat(text.length);
};

export const initAnalytics = () => {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as
    | string
    | undefined;
  const host =
    (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ??
    "https://us.i.posthog.com";
  if (!key || window.location.hostname === "localhost") return;
  posthog.init(key, {
    api_host: host,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true,
      // Selective unmasking for chat surfaces — see ``maskInputFn``
      // comment above. The function is only consulted when
      // maskAllInputs is true; without that flag rrweb skips it.
      maskInputFn,
    },
  });
  initialized = true;
};

export const identifyUser = (user: IdentifyProps) => {
  if (!initialized) return;
  posthog.identify(user.id, {
    email: user.email,
    company_id: user.company?.id,
    is_consultant: !!user.company?.isConsultant,
  });
};

export const resetAnalytics = () => {
  if (!initialized) return;
  posthog.reset();
};

export const captureEvent = (name: string, props?: Record<string, unknown>) => {
  if (!initialized) return;
  posthog.capture(name, props);
};

// ---------------------------------------------------------------------------
// Chat-content capture helper.
//
// Identical wire path to ``captureEvent`` (one ``posthog.capture`` call
// — no shadow SDK), but routes the payload through ``scrubPII`` for the
// three known content-bearing keys (``seed_prompt``, ``message_content``,
// ``response_text``) before sending. Non-string values for those keys
// are passed through unchanged — defensive against a caller shape error.
//
// Use this helper for every event in the ``chat.*`` namespace whose
// payload carries user or model prose. For events that only carry
// structured metadata (latencies, counts, ids) ``captureEvent`` is
// cheaper and sufficient.
// ---------------------------------------------------------------------------
export const captureChatContent = (
  name: string,
  props: Record<string, unknown>,
) => {
  if (!initialized) return;
  const scrubbed: Record<string, unknown> = { ...props };
  PII_SCRUB_KEYS.forEach((key) => {
    const value = scrubbed[key];
    if (typeof value === "string") {
      scrubbed[key] = scrubPII(value);
    }
  });
  posthog.capture(name, scrubbed);
};
